import React, { useCallback, useState } from 'react';
import { isVoidElement } from 'js/utils/DOM.util';
import { getClassNames } from 'js/utils/classNames.util';

import style from './AccessibilityElement.scss';

/**
 * @typedef {Object} AccessibilityElementProps
 * @property [tagName] {string | JSX.Element}
 * @property [tabIndex] {string | number}
 * @property [inline] {boolean}
 * @property [onBlur] {Function}
 * @property [onClick] {Function}
 * @property [onKeyUp] {Function}
 * @property [onKeyDown] {Function}
 * @property [className] {string}
 * @property [elementRef] {React.MutableRefObject | Function}
 * @property [accessibilityButtons] {string[]}
 * @property [ariaLiveLabel] {string}
 * @property [ariaLiveLabelPosition] {string}
 *
 * @param props {AccessibilityElementProps & Object<string, *>}
 */
export const AccessibilityElement = ({
    tagName: Component = 'div',
    tabIndex = 0,
    inline = false,
    onBlur,
    onClick,
    onKeyUp,
    onKeyDown,
    children,
    className,
    elementRef,
    accessibilityButtons = ['Enter'],
    ariaLiveLabel,
    ariaLiveLabelPosition = 'inside', // inside, outside
    ...otherProps
}) => {
    const handleOnKeyDown = useCallback((event) => {
        const { key, target, currentTarget } = event;

        onKeyDown?.(event);

        if (accessibilityButtons.includes(key) && target === currentTarget) {
            event.preventDefault();

            // force screen reader to voice push of the "Enter" key
            onClick && event.target.click();
        }
    }, [onKeyDown, onClick, accessibilityButtons]);

    // region :focus-visible support
    const [isFocused, setIsFocused] = useState(false);

    const handleOnKeyUp = useCallback((event) => {
        const { key } = event;

        onKeyUp?.(event);

        if (key === 'Tab') {
            setIsFocused(true);
        }
    }, [onKeyUp]);

    const handleOnBlur = useCallback((event) => {
        onBlur?.(event);

        setIsFocused(false);
    }, [onBlur]);
    // endregion

    const classNames = getClassNames([
        className,
        style.wrapper,
        inline ? style.inline : '',
        isFocused ? style.focused : ''
    ]);

    if (isVoidElement(Component)) {
        return (
            <>
                <Component
                    ref={elementRef}
                    onClick={onClick}
                    tabIndex={tabIndex}
                    onBlur={handleOnBlur}
                    className={classNames}
                    onKeyUp={handleOnKeyUp}
                    onKeyDown={handleOnKeyDown}
                    {...otherProps}
                />
                {ariaLiveLabel && ariaLiveLabelPosition === 'outside' && (
                    <span aria-live="polite" className="visually-hidden">{ariaLiveLabel}</span>
                )}
            </>
        );
    }

    return (
        <>
            <Component
                ref={elementRef}
                onClick={onClick}
                tabIndex={tabIndex}
                onBlur={handleOnBlur}
                className={classNames}
                onKeyUp={handleOnKeyUp}
                onKeyDown={handleOnKeyDown}
                {...otherProps}
            >
                {ariaLiveLabel && ariaLiveLabelPosition === 'inside' && (
                    <span aria-live="polite" className="visually-hidden">{ariaLiveLabel}</span>
                )}
                {children}
            </Component>
            {ariaLiveLabel && ariaLiveLabelPosition === 'outside' && (
                <span aria-live="polite" className="visually-hidden">{ariaLiveLabel}</span>
            )}
        </>
    );
};
